import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../../base/basecomponent';
import { UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';

import {
  Observable,
  Subject,
  finalize,
  interval,
  map,
  startWith,
  switchMap,
  BehaviorSubject,
  Subscription,
  combineLatest,
} from 'rxjs';
import {
  HourTypeUserFilter,
  WorktimeActionDTO,
  WorkTimeHourTypeGroup,
  WorkTimeShiftLocation,
  WorkTimeStartEndBreakDto,
} from '../../../../_models/work-time/workTime';
import { ListModel } from '../../../../_models/listmodel';
import { UserService } from '@app/areas/user/user.service';
import { CompanyService } from '@app/areas/company/company.service';
import { PersonService } from '@app/areas/person/services/person.service';
import { forkJoin } from 'rxjs';
import { IanaTimezoneService } from '@app/_services/iana-timezone.service';
import { WorkTimeErrorService } from '../../services/work-time-error.service';
import { HourTypeResponse } from '../../interfaces/work-time-hour-type-response';
import { UserFieldPermission } from '@app/areas/permissions/interfaces/permission';
import { Store, Select } from '@ngxs/store';
import { ComponentCanDeactivate } from '@guards/unsaved-changes.guard';
import { TasState, WorkShiftLocationEntryType } from '@app/_enums/workTime';
import { WorkTimeService } from '../../services/work-time.service';
import { HttpParams } from '@angular/common/http';
import { InvoicingService } from '@app/areas/invoicing/services/invoicing.service';
import { CostCentre } from '@app/_models/invoicing/invoicing';
import { ListModelState } from '@app/_store/list-model/list-model.state';
import { SettingService } from '@app/areas/setting/setting.service';
@Component({
  selector: 'work-time-indicator',
  templateUrl: './work-time-indicator.component.html',
  styleUrls: ['./work-time-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class WorkTimeIndicatorComponent
  extends BaseComponent
  implements OnInit, ComponentCanDeactivate
{
  public isLoading: boolean;

  public tasState = TasState;
  public tasStatus: TasState;

  @ViewChild('dropdown', { static: false }) workTimeIndicator: any;

  // Combine related state into a single BehaviorSubject
  private workTimeState = new BehaviorSubject<{
    duration: string;
    totalBalance: number;
    currentBalance: number;
    assignmentDuration: string;
    costCentreDuration: string;
    assignmentID: number | null;
    breakTypeId: number | null;
    costCentreID: number | null;
    workTimeAssignmentID: number | null;
    workTimeCostCentreID: number | null;
  }>({
    duration: '',
    totalBalance: 0,
    currentBalance: 0,
    assignmentDuration: '',
    costCentreDuration: '',
    assignmentID: null,
    breakTypeId: null,
    costCentreID: null,
    workTimeAssignmentID: null,
    workTimeCostCentreID: null
  });

  public showBalance = true;

  // Expose observables from the state
  public duration$ = this.workTimeState.pipe(
      switchMap(state => {
        return interval(15 * 1000).pipe(
          startWith(0),
          map(() => {
            return Math.max(new Date().getTime() - new Date(state.duration).getTime(), 0);
          })
        );
      })
    );
  public assignmentDuration$ = this.workTimeState.pipe(
      switchMap(state => {
        return interval(15 * 1000).pipe(
          startWith(0),
          map(() => {
            return Math.max(new Date().getTime() - new Date(state.assignmentDuration).getTime(), 0);
          })
        );
      })
    );
  public costCentreDuration$ = this.workTimeState.pipe(
      switchMap(state => {
        return interval(15 * 1000).pipe(
          startWith(0),
          map(() => {
            return Math.max(new Date().getTime() - new Date(state.costCentreDuration).getTime(), 0);
          })
        );
      })
    );
  public totalBalance$ = this.workTimeState.pipe(map(state => state.totalBalance));
  public startTime$ = this.workTimeState.pipe(map(state => state.duration));
  public costCentreStartTime$ = this.workTimeState.pipe(map(state => state.costCentreDuration));
  public currentBalance$ = this.workTimeState.pipe(map(state => state.currentBalance));
  public assignmentID$ = this.workTimeState.pipe(map(state => state.assignmentID));
  public costCentreID$ = this.workTimeState.pipe(map(state => state.costCentreID));
  public workTimeAssignmentID$ = this.workTimeState.pipe(map(state => state.workTimeAssignmentID));
  public workTimeCostCentreID$ = this.workTimeState.pipe(map(state => state.workTimeCostCentreID));


  // Timer subscription
  private timerSubscription: Subscription;

  public durationSubject: Subject<string> = new Subject<string>();
  public totalBalanceSubject: Subject<number> = new Subject<number>();
  public currentBalanceSubject: Subject<number> = new Subject<number>();
  public assignmentDurationSubject: Subject<string> = new Subject<string>();
  public costCentreDurationSubject: Subject<string> = new Subject<string>();
  public currentAssignmentIDSubject: Subject<number> = new Subject<number>();
  public currentCostCentreIDSubject: Subject<number> = new Subject<number>();
  public currentWorkTimeAssignmentIDSubject: Subject<number> = new Subject<number>();
  public currentWorkTimeCostCentreIDSubject: Subject<number> = new Subject<number>();

  public currentAssignmentID?: number;
  public currentCostCentreID?: number;
  public currentWorkTimeAssignmentID?: number;
  public currentWorkTimeCostCentreID?: number;
  public currentAssignmentStartTime?: string;
  public currentCostCentreStartTime?: string;
  public startTime: Observable<string>;
  public totalBalance: Observable<number>;
  public currentBalance: Observable<number>;
  public duration: Observable<number>;
  public assignmentDuration: Observable<number>;
  public costCentreDuration: Observable<number>;
  public assignmentID: Observable<number | null>;
  public costCentreID: Observable<number | null>;
  public workTimeAssignmentID: Observable<number | null>;
  public workTimeCostCentreID: Observable<number | null>;
  public costCentreName: Observable<string>;
  public breakName: string;
  public breakTypes: ListModel[] = [];
  public costCentres$: Observable<ListModel[]> = inject(Store).select(ListModelState.costCentres);
  public showBreakDialog: boolean = false;
  public requireExplanation: boolean = false;
  public showCostCentreDialog: boolean = false;
  public showShiftDialog: boolean = false;
  public showDialog: boolean = false;
  public dialogType: number = 0;
  public breakForm: UntypedFormGroup;
  public costCentreForm: UntypedFormGroup;
  public shiftForm: UntypedFormGroup;
  public tasEnabled: boolean = false;
  public costCentreEnabled: boolean = false;
  public balanceEnabled: boolean = false;

  // Computed observable for cost centre name
  public costCentreName$: Observable<string> = combineLatest(
    {
      workTimeState: this.workTimeState,
      costCentres: this.costCentres$,
    }
  ).pipe(
    map((res) => {
      if (res.workTimeState.costCentreID) {
        const costCentre = res.costCentres.find(x => x.id === res.workTimeState.costCentreID);
        return costCentre ? ` ${costCentre.name}` : '';
      }
      return '';
    })
  );
  constructor(
    private workTimeService: WorkTimeService,
    private invoicingService: InvoicingService,
    private userService: UserService,
    private settingService: SettingService,
    private companyService: CompanyService,
    private personService: PersonService,
    private timezoneService: IanaTimezoneService,
    private store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscription.add(
    this.settingService.moduleActive("worktime_monitoring").subscribe(x => {
      this.tasEnabled = (x == 1)
    }));
    this.subscription.add(
    this.settingService.getSettingValue("workTimeCostCentreEnabled").subscribe(x => {
      this.costCentreEnabled = (x == 'true')
    }));
    this.subscription.add(
    this.settingService.getSettingValue("workTimeBalanceEnabled").subscribe(x => {
      this.balanceEnabled = (x == 'true')
    }));

    this.getStatus();
    this.initializeForms();
    this.getBreakTypes();
    this.startTimer();
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  private initializeForms(): void {
    this.breakForm = this.formBuilder.group({
      breakType: [null, Validators.required],
      explanation: [null],
    });
    this.costCentreForm = this.formBuilder.group({
      costCentre: [null, Validators.required],
      costCentreExplanation: [null],
    });
    this.shiftForm = this.formBuilder.group({
      explanationForStartTime: [null],
      explanationForEndTime: [null],
    });
  }

  private startTimer(): void {
    this.timerSubscription = interval(15 * 1000).pipe(
      startWith(0)
    ).subscribe(() => {
      const currentState = this.workTimeState.value;
      const now = new Date().getTime();

      this.workTimeState.next({
        ...currentState,
        duration: currentState.duration ? new Date(currentState.duration).toISOString() : '',
        assignmentDuration: currentState.assignmentDuration ? new Date(currentState.assignmentDuration).toISOString() : '',
        costCentreDuration: currentState.costCentreDuration ? new Date(currentState.costCentreDuration).toISOString() : ''
      });
    });
  }

  toggleDialog() {
    this.showDialog = !this.showDialog;
  }

  getStatus() {
    this.workTimeService.getWorktimeStatus().subscribe({
      next: (response) => {
        this.tasStatus = response;
        if (response === TasState.Working) {
          this.getShiftStartTime();
        } else if (response === TasState.Break) {
          this.getBreakInfo();
        }
      },
      error: (error) => {
        this.errorHandler.applyGeneralErrors(error);
      },
    });
  }

  getShiftStartTime() {
    const params = new HttpParams()
      .set('fetchCurrentAssignmentID', true)
      .set('fetchCurrentCostCentreID', true);

    this.workTimeService.getWorktimeShiftInfo(params).subscribe({
      next: (response) => {
        this.workTimeState.next({
          duration: response.startTime,
          totalBalance: response.totalBalanceMinutes,
          currentBalance: response.currentBalanceMinutes,
          assignmentDuration: response.currentAssignmentStartTime,
          costCentreDuration: response.currentCostCentreStartTime,
          assignmentID: response.currentAssignmentID,
          breakTypeId: null,
          costCentreID: response.currentCostCentreID,
          workTimeAssignmentID: response.currentWorkTimeAssignmentID,
          workTimeCostCentreID: response.currentWorkTimeCostCentreID
        });
      },
      error: (error) => {
        this.errorHandler.applyGeneralErrors(error);
      },
    });
  }

  getBreakInfo() {
    const params = new HttpParams().set('lang', this.translate.currentLang);

    this.workTimeService.getWorktimeBreakInfo(params).subscribe({
      next: (response) => {
        this.breakName = response.name;
        this.workTimeState.next({...this.workTimeState.value,
          duration: response.startTime,
          breakTypeId: response.breakTypeId,
          costCentreID: null,
          workTimeAssignmentID: null,
          workTimeCostCentreID: null

        });
      },
      error: (error) => {
        this.errorHandler.applyGeneralErrors(error);
      },
    });
  }

  getBreakTypes() {
    this.workTimeService
      .getWorkTimeBreakTypeList(this.translate.currentLang)
      .subscribe(
        (data) => {
          this.breakTypes = data;
        },
        (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      );
  }

  cancelBreak() {
    this.showBreakDialog = false;
    this.breakForm.reset();
  }
  closeDialog() {
    this.showBreakDialog = false;
    this.showDialog = false;
  }

  toggleBreakDialog() {
    if(this.dialogType == 1)
      this.showDialog = !this.showDialog;
    else
      if(this.showDialog)
        this.dialogType = 1;
      else {
        this.showDialog = !this.showDialog;
        this.dialogType = 1;
      }
  }
  toggleCostCentreDialog() {
    if(this.dialogType == 2)
      this.showDialog = !this.showDialog;
    else
      if(this.showDialog)
        this.dialogType = 2;
      else {
        this.showDialog = !this.showDialog;
        this.dialogType = 2;
      }
  }
  startCostCentre() {
    if (this.costCentreForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.workTimeService
      .startWorkTimeCostCentre(this.costCentreForm.get('costCentre').value)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showDialog = false;
          this.dialogType = 0;
          this.costCentreForm.reset();
          this.workTimeIndicator.hide();
          this.getStatus();
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }
  finishCostCentre() {
    const currentState = this.workTimeState.value;
    if (!currentState.workTimeCostCentreID) {
      return;
    }
    let explanation = this.costCentreForm.get('costCentreExplanation').value;

    this.workTimeService
      .finishWorkTimeCostCentre(currentState.workTimeCostCentreID, explanation)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showDialog = false;
          this.dialogType = 0;
          this.costCentreForm.reset();
          this.workTimeIndicator.hide();
          this.getStatus();
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }

  startBreak() {
    if (this.breakForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.workTimeService
      .startWorkTimeBreak(this.breakForm.get('breakType').value)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showDialog = false;
          this.dialogType = 0;
          this.breakForm.reset();
          this.workTimeIndicator.hide();
          this.getStatus();
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }

  finishBreak() {
    this.isLoading = true;

    const dto: WorkTimeStartEndBreakDto = {
      breakTypeId: this.workTimeState.value.breakTypeId,
      ianaTimeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
      explanationForBreak: this.breakForm.get('explanation').value,
    };
    this.workTimeService
      .finishWorkTimeBreak(dto)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.breakName = null;
          this.breakForm.reset();
          this.showDialog = false;
          this.dialogType = 0;
          this.workTimeIndicator.hide();
          this.getStatus();
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }

  startShift(explanationForTime?: string) {
    this.isLoading = true;

    const startDto: WorktimeActionDTO = {
      ianaTimeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if((this.shiftForm.get("explanationForStartTime").value ?? "").trim() != "" )
      startDto.explanationForTime = this.shiftForm.get("explanationForStartTime").value

    this.workTimeService
      .startWorkTimeShift(startDto)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: async (response) => {
          if (response === 'explanation_required') {
            this.workTimeIndicator.show();
            this.showDialog = true;
            this.dialogType = 3;
            this.shiftForm.reset();
          } else {
            this.workTimeIndicator.hide();
            this.showDialog = false;
            this.dialogType = 0;
            this.shiftForm.reset();
            this.getStatus();
            //this.saveShiftLocation(WorkShiftLocationEntryType.WorkShiftStart);
          }
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }

  finishShift(explanationForTime?: string) {
    this.isLoading = true;

    const finishDto: WorktimeActionDTO = {
      ianaTimeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if((this.shiftForm.get("explanationForEndTime").value ?? "").trim() != "")
      finishDto.explanationForTime = this.shiftForm.get("explanationForEndTime").value;

    this.workTimeService
      .finishWorkTimeShift(finishDto)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: async (response) => {
          if (response === 'explanation_required') {
            this.workTimeIndicator.show();
            this.showDialog = true;
            this.dialogType = 3;
          } else {
            this.showDialog = false;
            this.dialogType = 0;
            this.workTimeIndicator.hide();
            this.shiftForm.reset()
            this.getStatus();
          }
        },
        error: (error) => {
          this.errorHandler.applyGeneralErrors(error);
        },
      });
  }

  getMinWidth(): number{
    switch(this.tasStatus) {
      case TasState.NotActive:
        return 0;
      case TasState.NotWorking:
        return 0;
      case TasState.Working:
        return 250;
      case TasState.Break:
        return 250;
      case TasState.OnCallDuty:
        return 0;
      default:
        return 0;
    }
  }
  getTasColor(){
    switch(this.tasStatus) {
      case TasState.NotActive:
        return '#717a82';
      case TasState.NotWorking:
        return '#717a82';
      case TasState.Working:
        return '#38c07e';
      case TasState.Break:
        return 'yellow';
      case TasState.OnCallDuty:
        return 'red';
      default:
        return '#717a82';
    }
  }


  canDeactivate(): boolean {
    return true;
  }
  cancelShift(){
    this.shiftForm.reset();
    this.showDialog = false;
    this.dialogType = 0;
  }
}
